// src/components/roleplay/TeamDashboard.js
// Manager roll-up over roleplay sessions: summary tiles, a per-rep leaderboard,
// and a recent-sessions table. Self-contained — no outer page chrome.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { trackLabel, difficultyLabel, formatUsd } from '../../config/roleplay';
import { subscribeAllSessions } from '../../data/roleplay';

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const SectionSubtitle = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0 0 16px 0;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const Tile = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
`;

const TileValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
`;

const TileLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const TableWrap = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 32px;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  min-width: 560px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 16px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  color: #1e293b;
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #0e8c6a;
  background: #e6f5f0;
`;

const ScoreBadge = styled(Badge)`
  color: ${(props) => (props.$score >= 4 ? '#0e8c6a' : props.$score >= 2.5 ? '#d97706' : '#dc2626')};
  background: ${(props) => (props.$score >= 4 ? '#e6f5f0' : props.$score >= 2.5 ? '#fef3c7' : '#fef2f2')};
`;

const Muted = styled.span`
  color: #94a3b8;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 32px;
  text-align: center;
`;

const ErrorState = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  margin: 8px 0 24px;
`;

function formatDate(ts) {
  const d = ts?.toDate?.();
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function sortableTime(ts) {
  const d = ts?.toDate?.();
  return d ? d.getTime() : 0;
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

function TeamDashboard({ user }) { // eslint-disable-line no-unused-vars
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const unsub = subscribeAllSessions(
      (rows) => {
        setSessions(rows);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        setLoadError(
          err?.code === 'permission-denied'
            ? "You don't have permission to view team sessions. Contact an admin if this seems wrong."
            : 'Could not load sessions. Please refresh and try again.'
        );
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const totalSessions = sessions.length;

  const repStats = useMemo(() => {
    const byRep = new Map();
    for (const s of sessions) {
      const key = (s.repEmail || s.repName || 'unknown').toLowerCase();
      if (!byRep.has(key)) {
        byRep.set(key, {
          key,
          repName: s.repName || s.repEmail || 'Unknown',
          repEmail: s.repEmail || '',
          count: 0,
          scoreSum: 0,
          scoreCount: 0,
          costSum: 0,
          lastActivity: null,
        });
      }
      const entry = byRep.get(key);
      entry.count += 1;
      if (typeof s.overall === 'number') {
        entry.scoreSum += s.overall;
        entry.scoreCount += 1;
      }
      if (typeof s.costUsd === 'number') entry.costSum += s.costUsd;
      const t = sortableTime(s.createdAt);
      if (!entry.lastActivity || t > entry.lastActivity) entry.lastActivity = t;
    }
    return Array.from(byRep.values()).sort((a, b) => b.count - a.count);
  }, [sessions]);

  const distinctReps = repStats.length;

  const avgOverall = useMemo(() => {
    const scored = sessions.filter((s) => typeof s.overall === 'number');
    if (!scored.length) return null;
    return round1(scored.reduce((sum, s) => sum + s.overall, 0) / scored.length);
  }, [sessions]);

  const totalCost = useMemo(
    () => sessions.reduce((sum, s) => sum + (typeof s.costUsd === 'number' ? s.costUsd : 0), 0),
    [sessions]
  );

  const recentSessions = useMemo(() => {
    return [...sessions]
      .sort((a, b) => sortableTime(b.createdAt) - sortableTime(a.createdAt))
      .slice(0, 20);
  }, [sessions]);

  return (
    <div>
      <SectionTitle>Team dashboard</SectionTitle>
      <SectionSubtitle>Roll-up of every rep's roleplay activity.</SectionSubtitle>

      {loading && <EmptyState>Loading sessions...</EmptyState>}
      {!loading && loadError && <ErrorState>{loadError}</ErrorState>}

      {!loading && !loadError && (
        <>
          <TileGrid>
            <Tile>
              <TileValue>{totalSessions}</TileValue>
              <TileLabel>Total sessions</TileLabel>
            </Tile>
            <Tile>
              <TileValue>{distinctReps}</TileValue>
              <TileLabel>Distinct reps</TileLabel>
            </Tile>
            <Tile>
              <TileValue>{avgOverall != null ? avgOverall : '—'}</TileValue>
              <TileLabel>Average overall score</TileLabel>
            </Tile>
            <Tile>
              <TileValue>{formatUsd(totalCost)}</TileValue>
              <TileLabel>Total est. LLM cost</TileLabel>
            </Tile>
          </TileGrid>

          {totalSessions === 0 ? (
            <EmptyState>No sessions logged yet.</EmptyState>
          ) : (
            <>
              <TableWrap>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Rep</Th>
                        <Th>Calls</Th>
                        <Th>Avg overall</Th>
                        <Th>Est. cost</Th>
                        <Th>Last activity</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {repStats.map((r) => (
                        <tr key={r.key}>
                          <Td>
                            {r.repName}
                            {r.repEmail && r.repEmail !== r.repName && (
                              <>
                                {' '}
                                <Muted>({r.repEmail})</Muted>
                              </>
                            )}
                          </Td>
                          <Td>{r.count}</Td>
                          <Td>
                            {r.scoreCount ? (
                              <ScoreBadge $score={r.scoreSum / r.scoreCount}>
                                {round1(r.scoreSum / r.scoreCount)}
                              </ScoreBadge>
                            ) : (
                              <Muted>—</Muted>
                            )}
                          </Td>
                          <Td>{r.costSum > 0 ? formatUsd(r.costSum) : <Muted>—</Muted>}</Td>
                          <Td>{r.lastActivity ? formatDate({ toDate: () => new Date(r.lastActivity) }) : <Muted>—</Muted>}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              </TableWrap>

              <SectionTitle>Recent sessions</SectionTitle>
              <SectionSubtitle>The latest {recentSessions.length} calls logged.</SectionSubtitle>
              <TableWrap>
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Rep</Th>
                        <Th>Persona</Th>
                        <Th>Track</Th>
                        <Th>Difficulty</Th>
                        <Th>Overall</Th>
                        <Th>Cost</Th>
                        <Th>Date</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSessions.map((s) => (
                        <tr key={s.id}>
                          <Td>{s.repName || s.repEmail || <Muted>Unknown</Muted>}</Td>
                          <Td>{s.personaTitle || <Muted>—</Muted>}</Td>
                          <Td>{s.track ? trackLabel(s.track) : <Muted>—</Muted>}</Td>
                          <Td>{s.difficulty ? difficultyLabel(s.difficulty) : <Muted>—</Muted>}</Td>
                          <Td>
                            {typeof s.overall === 'number' ? (
                              <ScoreBadge $score={s.overall}>{round1(s.overall)}</ScoreBadge>
                            ) : (
                              <Muted>—</Muted>
                            )}
                          </Td>
                          <Td>{typeof s.costUsd === 'number' ? formatUsd(s.costUsd) : <Muted>—</Muted>}</Td>
                          <Td>{formatDate(s.createdAt)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              </TableWrap>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TeamDashboard;
